import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getCapitalConsolidations,
  saveCapitalConsolidationsBatch,
} from '../../api/capitalConsolidations';
import { useFunds } from '../../hooks/useFunds';
import type { CapitalConsolidation } from '../../types/CapitalConsolidation';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

// fundId + dDate are the COMPOSITE PK — editable only while the row is
// `__status === 'added'` (mirrors CalcMethodMapPage's editableOnAdd rule).
const editableOnAdd = (
  params: EditableCallbackParams<CapitalConsolidation>,
) => (params.data as { __status?: string } | undefined)?.__status === 'added';

const newCapitalConsolidationDefaults = (): Partial<CapitalConsolidation> => ({
  fundId: 0,
  fundName: null,
  dDate: null as unknown as string,
  consolidationRatio: 0,
  redemptionUnits: 0,
});

export function CapitalConsolidationsPage() {
  // Tenant-scoped lookup. Also doubles as the fix for the hard-refresh
  // X-Tenant-Id race: useFunds calls setTenantId itself (from the user object,
  // not from AuthContext's own effect) before firing its fetch — see its
  // module comment. Gating LopCrudPage's mount (and its getRows call) behind
  // fundsLoading guarantees the tenant header is already set by the time the
  // capital-consolidations request goes out. Mirrors BankAccountsPage.
  const { data: funds, loading: fundsLoading, error: fundsError } = useFunds();

  const columnDefs = useMemo<ColDef<CapitalConsolidation>[]>(() => {
    if (!funds) return [];

    // fundId is a REQUIRED FK (part of the PK) — no none option. Keyed by a
    // unique "<id> - <name>" label (mirrors CurrencyNonSettlementDatesPage's
    // required crcyId dropdown); orphan ids fall back to the raw id so dirty
    // data stays visible/editable.
    const fundLabelById = new Map<number, string>(
      funds.map((f) => [f.fundId, `${f.fundId} - ${f.fundName}`]),
    );
    const fundIdByLabel = new Map<string, number>(
      funds.map((f) => [`${f.fundId} - ${f.fundName}`, f.fundId]),
    );
    const fundLabels = Array.from(fundIdByLabel.keys());

    return [
      {
        field: 'fundId',
        headerName: t('capitalConsolidations.columns.fundId'),
        width: 240,
        editable: editableOnAdd,
        // cellDataType: 'text' is REQUIRED on id<->name dropdowns — without it
        // AgGrid infers number from the field type and coerces the picked
        // label back to NaN before the valueSetter runs.
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: fundLabels },
        valueGetter: (p: ValueGetterParams<CapitalConsolidation>) => {
          if (!p.data) return '';
          return fundLabelById.get(p.data.fundId) ?? String(p.data.fundId);
        },
        valueSetter: (p: ValueSetterParams<CapitalConsolidation>) => {
          if (!p.data) return false;
          const id = fundIdByLabel.get(p.newValue as string);
          if (id === undefined) return false;
          p.data.fundId = id;
          return true;
        },
        sort: 'asc',
      },
      {
        field: 'fundName',
        headerName: t('capitalConsolidations.columns.fundName'),
        flex: 1,
        minWidth: 200,
        editable: false,
      },
      {
        field: 'dDate',
        headerName: t('capitalConsolidations.columns.dDate'),
        width: 160,
        editable: editableOnAdd,
        ...dateColumnProps<CapitalConsolidation>('dDate'),
      },
      {
        field: 'consolidationRatio',
        headerName: t('capitalConsolidations.columns.consolidationRatio'),
        width: 160,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'redemptionUnits',
        headerName: t('capitalConsolidations.columns.redemptionUnits'),
        width: 160,
        editable: true,
        cellDataType: 'number',
      },
    ];
  }, [funds]);

  if (fundsError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('capitalConsolidations.errorPrefix', { message: fundsError })}
        </div>
      </div>
    );
  }

  if (fundsLoading || !funds) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<CapitalConsolidation>
      resourceCode="capitalConsolidations"
      titleKey="capitalConsolidations.pageTitle"
      idField="idKey"
      getRows={getCapitalConsolidations}
      saveBatch={(added, modified, deletedIds) =>
        saveCapitalConsolidationsBatch(added, modified, deletedIds as string[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newCapitalConsolidationDefaults}
    />
  );
}
